<script setup>
// BonBoZ: panel "Cuộc trò chuyện mới" của hộp thư Zalo, phủ lên cột danh sách
// hội thoại. Ba màn: chính (tìm SĐT + bạn bè), lời mời đến, tạo nhóm.
// Mọi lời gọi đi qua Rails → cầu nối (api/bonboz/zalo.js).
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { watchDebounced } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import ZaloAPI from 'dashboard/api/bonboz/zalo';
import { loiApi } from 'dashboard/api/pageConfigs';
import { loadPageConfigs } from 'dashboard/helper/pageConfig';
import {
  chuanHoaSdt,
  locBanBe,
  loiMoiMacDinh,
} from 'dashboard/helper/zaloKetBan';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: { type: Object, required: true }, // inbox thô (snake_case)
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const router = useRouter();
const id = computed(() => props.inbox.id);
const T = k => t(`BONBOZ.CUOC_TRO_CHUYEN_MOI.${k}`);
const baoLoi = e => useAlert(loiApi(e));

const man = ref('chinh'); // chinh | loi_moi | nhom
const dangXuLy = ref(false);

const moHoiThoai = conversationId => {
  if (!conversationId) return;
  emit('close');
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};

// Bọc lời gọi: bật spinner, lỗi thì alert, trả null.
const chay = async fn => {
  dangXuLy.value = true;
  try {
    return await fn();
  } catch (e) {
    baoLoi(e);
    return null;
  } finally {
    dangXuLy.value = false;
  }
};

// `nguoi` = dòng panel đang hiện ({ uid, ten, avatar }), từ `/tim_sdt` hoặc danh sách
// bạn bè. Gửi kèm tên + avatar: khách CHƯA KẾT BẠN thì Zalo không cho bridge đọc hồ sơ,
// không có cái này thì thẻ hội thoại mang tên là dãy số uid.
const hoSoKhach = nguoi => ({
  ten: nguoi?.ten || '',
  avatar: nguoi?.avatar || '',
});

const nhanTin = async nguoi => {
  const res = await chay(() =>
    ZaloAPI.nhanTin(id.value, nguoi.uid, hoSoKhach(nguoi))
  );
  if (res) moHoiThoai(res.data.conversation_id);
};

// --- Bạn bè (dùng chung cho màn chính và tạo nhóm) --------------------------
const banBe = ref([]);
const dangTaiBanBe = ref(false);
const tuKhoa = ref('');
const banBeLoc = computed(() => locBanBe(banBe.value, tuKhoa.value));

onMounted(async () => {
  dangTaiBanBe.value = true;
  try {
    banBe.value = (await ZaloAPI.banBe(id.value)).data.ban_be || [];
  } catch (e) {
    baoLoi(e);
  } finally {
    dangTaiBanBe.value = false;
  }
});

// --- Tìm SĐT ------------------------------------------------------------------
const sdt = ref('');
const dangTim = ref(false);
const ketQua = ref(undefined); // undefined = chưa tìm, null = không thấy
const soanLoiMoi = ref(false);
const loiNhan = ref('');

watchDebounced(
  sdt,
  async value => {
    const so = chuanHoaSdt(value);
    soanLoiMoi.value = false;
    ketQua.value = undefined;
    if (!so) return;
    dangTim.value = true;
    try {
      const { data } = await ZaloAPI.timSdt(id.value, so);
      if (so === chuanHoaSdt(sdt.value)) ketQua.value = data.nguoi;
    } catch (e) {
      baoLoi(e);
    } finally {
      dangTim.value = false;
    }
  },
  { debounce: 500 }
);

const moSoanLoiMoi = async () => {
  const cauHinh = await loadPageConfigs();
  loiNhan.value = loiMoiMacDinh(
    cauHinh?.[String(id.value)],
    props.inbox.name,
    t
  );
  soanLoiMoi.value = true;
};

const guiLoiMoi = async () => {
  const res = await chay(() =>
    ZaloAPI.guiLoiMoi(
      id.value,
      ketQua.value.uid,
      loiNhan.value,
      hoSoKhach(ketQua.value)
    )
  );
  if (!res) return;
  useAlert(T('DA_GUI_LOI_MOI'));
  moHoiThoai(res.data.conversation_id);
};

// --- Lời mời đến --------------------------------------------------------------
const loiMoi = ref([]);
const dangTaiLoiMoi = ref(false);
const dangTraLoi = ref(null); // uid đang gửi

watch(man, async value => {
  tuKhoa.value = '';
  if (value !== 'loi_moi') return;
  loiMoi.value = [];
  dangTaiLoiMoi.value = true;
  try {
    loiMoi.value = (await ZaloAPI.loiMoiDen(id.value)).data.loi_moi || [];
  } catch (e) {
    baoLoi(e);
  } finally {
    dangTaiLoiMoi.value = false;
  }
});

const traLoi = async (nguoi, dongY) => {
  const { uid } = nguoi;
  dangTraLoi.value = uid;
  try {
    await (dongY
      ? ZaloAPI.dongY(id.value, uid, hoSoKhach(nguoi))
      : ZaloAPI.tuChoi(id.value, uid));
    loiMoi.value = loiMoi.value.filter(m => m.uid !== uid);
  } catch (e) {
    baoLoi(e);
  } finally {
    dangTraLoi.value = null;
  }
};

// --- Tạo nhóm -----------------------------------------------------------------
const TOI_DA_TEN = 50;
const TOI_DA_THANH_VIEN = 100;
const tenNhom = ref('');
const daChon = ref([]);
const coTheTaoNhom = computed(
  () => tenNhom.value.trim() && daChon.value.length && !dangXuLy.value
);

const chon = (uid, bat) => {
  if (!bat) daChon.value = daChon.value.filter(u => u !== uid);
  else if (daChon.value.length < TOI_DA_THANH_VIEN) daChon.value.push(uid);
};

const thietLapLai = () => {
  tenNhom.value = '';
  daChon.value = [];
  tuKhoa.value = '';
};

const taoNhom = async () => {
  const res = await chay(() =>
    ZaloAPI.taoNhom(id.value, tenNhom.value.trim(), daChon.value)
  );
  if (!res) return;
  const loi = res.data.loi_thanh_vien || [];
  if (loi.length) {
    const ten = loi.map(u => banBe.value.find(b => b.uid === u)?.ten || u);
    useAlert(
      t('BONBOZ.CUOC_TRO_CHUYEN_MOI.LOI_THANH_VIEN', { ten: ten.join(', ') })
    );
  }
  moHoiThoai(res.data.conversation_id);
};

const tieuDe = computed(
  () =>
    ({
      chinh: T('TIEU_DE'),
      loi_moi: T('LOI_MOI_KET_BAN'),
      nhom: T('TAO_NHOM_MOI'),
    })[man.value]
);
const oTimClass =
  'reset-base w-full !h-8 !m-0 !px-3 !text-sm !bg-n-slate-3 !rounded-full !outline-0 text-n-slate-12 placeholder:text-n-slate-10 focus-visible:ring-2 focus-visible:ring-n-brand';
</script>

<template>
  <div
    class="absolute inset-0 z-20 flex flex-col bg-n-surface-1 text-n-slate-12"
  >
    <!-- Đầu panel: X ở màn chính, ← ở màn con -->
    <div
      class="flex items-center gap-2 px-3 h-12 border-b border-n-weak shrink-0"
    >
      <NextButton
        v-if="man !== 'chinh'"
        v-tooltip.bottom="T('QUAY_LAI')"
        icon="i-lucide-arrow-left"
        slate
        ghost
        sm
        @click="man = 'chinh'"
      />
      <span class="flex-1 min-w-0 font-medium text-sm truncate">{{
        tieuDe
      }}</span>
      <NextButton
        v-if="man === 'chinh'"
        v-tooltip.bottom="T('DONG')"
        icon="i-lucide-x"
        slate
        ghost
        sm
        @click="emit('close')"
      />
    </div>

    <!-- MÀN CHÍNH -->
    <template v-if="man === 'chinh'">
      <div class="flex flex-col gap-2 p-3 border-b border-n-weak shrink-0">
        <div class="flex items-center gap-1">
          <PhoneNumberInput
            v-model="sdt"
            class="flex-1 min-w-0"
            :placeholder="T('NHAP_SDT')"
          />
          <NextButton
            v-tooltip.bottom="T('LOI_MOI_KET_BAN')"
            icon="i-lucide-user-round-check"
            slate
            faded
            sm
            @click="man = 'loi_moi'"
          />
          <NextButton
            v-tooltip.bottom="T('TAO_NHOM_MOI')"
            icon="i-lucide-users-round"
            slate
            faded
            sm
            @click="man = 'nhom'"
          />
        </div>

        <div v-if="dangTim" class="flex justify-center py-2"><Spinner /></div>
        <p
          v-else-if="ketQua === null"
          class="m-0 py-2 text-sm text-center text-n-slate-11"
        >
          {{ T('KHONG_TIM_THAY') }}
        </p>
        <div v-else-if="ketQua" class="flex flex-col gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <Avatar
              :src="ketQua.avatar || ''"
              :name="ketQua.ten || '?'"
              :size="36"
              rounded-full
            />
            <span class="flex-1 min-w-0 text-sm font-medium truncate">{{
              ketQua.ten
            }}</span>
            <NextButton
              :label="T('NHAN_TIN')"
              slate
              faded
              xs
              :disabled="dangXuLy"
              @click="nhanTin(ketQua)"
            />
            <NextButton
              v-if="!soanLoiMoi"
              :label="T('KET_BAN')"
              xs
              :disabled="dangXuLy"
              @click="moSoanLoiMoi"
            />
          </div>
          <template v-if="soanLoiMoi">
            <textarea
              v-model="loiNhan"
              rows="3"
              class="reset-base w-full !m-0 p-2 text-sm rounded-lg !bg-n-alpha-black2 outline outline-1 outline-n-weak focus:outline-n-brand text-n-slate-12 resize-none"
            />
            <div class="flex justify-end gap-2">
              <NextButton
                :label="T('HUY')"
                slate
                faded
                xs
                @click="soanLoiMoi = false"
              />
              <NextButton
                :label="T('GUI_LOI_MOI')"
                xs
                :is-loading="dangXuLy"
                :disabled="dangXuLy"
                @click="guiLoiMoi"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="flex items-center gap-2 px-3 pt-3 pb-2 shrink-0">
        <span class="text-sm font-medium">{{ T('DANH_SACH_BAN_BE') }}</span>
        <span class="px-1.5 rounded-full text-xs bg-n-slate-3 text-n-slate-11">
          {{ banBe.length }}
        </span>
      </div>
      <div class="px-3 pb-2 shrink-0">
        <input
          v-model="tuKhoa"
          type="search"
          :placeholder="T('TIM_BAN_BE')"
          :class="oTimClass"
        />
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div v-if="dangTaiBanBe" class="flex justify-center py-4">
          <Spinner />
        </div>
        <p
          v-else-if="!banBeLoc.length"
          class="m-0 py-4 text-sm text-center text-n-slate-11"
        >
          {{ T('KHONG_CO_BAN_BE') }}
        </p>
        <button
          v-for="b in banBeLoc"
          :key="b.uid"
          type="button"
          class="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="dangXuLy"
          @click="nhanTin(b)"
        >
          <Avatar
            :src="b.avatar || ''"
            :name="b.ten || '?'"
            :size="32"
            rounded-full
          />
          <span class="min-w-0">
            <span class="block text-sm truncate">{{ b.ten }}</span>
            <span class="block text-xs text-n-slate-11">{{
              b.ngay_sinh || '–'
            }}</span>
          </span>
        </button>
      </div>
    </template>

    <!-- LỜI MỜI KẾT BẠN -->
    <div v-else-if="man === 'loi_moi'" class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="dangTaiLoiMoi" class="flex justify-center py-4">
        <Spinner />
      </div>
      <p
        v-else-if="!loiMoi.length"
        class="m-0 py-6 text-sm text-center text-n-slate-11"
      >
        {{ T('KHONG_CO_LOI_MOI') }}
      </p>
      <div
        v-for="m in loiMoi"
        :key="m.uid"
        class="flex items-center gap-2 px-3 py-2"
      >
        <Avatar
          :src="m.avatar || ''"
          :name="m.ten || '?'"
          :size="36"
          rounded-full
        />
        <span class="flex-1 min-w-0">
          <span class="block text-sm truncate">{{ m.ten }}</span>
          <span class="block text-xs text-n-slate-11">{{
            m.ngay_sinh || '–'
          }}</span>
        </span>
        <NextButton
          :label="T('TU_CHOI')"
          slate
          faded
          xs
          :disabled="dangTraLoi === m.uid"
          @click="traLoi(m, false)"
        />
        <NextButton
          :label="T('DONG_Y')"
          xs
          :disabled="dangTraLoi === m.uid"
          @click="traLoi(m, true)"
        />
      </div>
    </div>

    <!-- TẠO NHÓM MỚI -->
    <template v-else>
      <div class="flex flex-col items-center gap-3 p-3 shrink-0">
        <span
          class="flex items-center justify-center size-14 rounded-full bg-n-slate-3 text-n-slate-10"
        >
          <i class="i-lucide-camera size-6" aria-hidden="true" />
        </span>
        <div class="relative w-full">
          <input
            v-model="tenNhom"
            :maxlength="TOI_DA_TEN"
            :placeholder="T('TEN_NHOM')"
            class="reset-base w-full !h-9 !m-0 !pl-3 !pr-14 !text-sm !bg-transparent !rounded-none !border-0 !border-b !border-n-weak focus:!border-n-brand text-n-slate-12 placeholder:text-n-slate-10"
          />
          <span class="absolute right-0 top-2 text-xs text-n-slate-10">
            {{ tenNhom.length }}/{{ TOI_DA_TEN }}
          </span>
        </div>
        <input
          v-model="tuKhoa"
          type="search"
          :placeholder="T('TIM_BAN_BE')"
          :class="oTimClass"
        />
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div v-if="dangTaiBanBe" class="flex justify-center py-4">
          <Spinner />
        </div>
        <label
          v-for="b in banBeLoc"
          :key="b.uid"
          class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-n-alpha-1"
        >
          <Checkbox
            :model-value="daChon.includes(b.uid)"
            :disabled="
              !daChon.includes(b.uid) && daChon.length >= TOI_DA_THANH_VIEN
            "
            @update:model-value="v => chon(b.uid, v)"
          />
          <Avatar
            :src="b.avatar || ''"
            :name="b.ten || '?'"
            :size="32"
            rounded-full
          />
          <span class="min-w-0">
            <span class="block text-sm truncate">{{ b.ten }}</span>
            <span class="block text-xs text-n-slate-11">{{
              b.ngay_sinh || '–'
            }}</span>
          </span>
        </label>
      </div>
      <div
        class="flex items-center gap-2 px-3 py-2 border-t border-n-weak shrink-0"
      >
        <span class="flex-1 text-xs text-n-slate-11">
          {{
            t('BONBOZ.CUOC_TRO_CHUYEN_MOI.DA_CHON', {
              so: daChon.length,
              toiDa: TOI_DA_THANH_VIEN,
            })
          }}
        </span>
        <NextButton
          :label="T('THIET_LAP_LAI')"
          slate
          faded
          xs
          @click="thietLapLai"
        />
        <NextButton
          :label="T('TAO_NHOM')"
          xs
          :is-loading="dangXuLy"
          :disabled="!coTheTaoNhom"
          @click="taoNhom"
        />
      </div>
    </template>
  </div>
</template>
